/** Block schema for fillable document templates (shared builder + renderer). */

export const BLOCK_TYPES = {
  HEADING: "heading",
  PARAGRAPH: "paragraph",
  CALLOUT: "callout",
  DIVIDER: "divider",
  TEXT_FIELD: "text_field",
  TEXTAREA: "textarea",
  NUMBER: "number",
  DATE: "date",
  CHECKBOX: "checkbox",
  SELECT: "select",
  CHECKLIST: "checklist",
  TABLE: "table",
  SIGNATURE: "signature",
};

export const BLOCK_PALETTE = [
  { type: BLOCK_TYPES.HEADING, labelKey: "documentForms.blocks.heading" },
  { type: BLOCK_TYPES.PARAGRAPH, labelKey: "documentForms.blocks.paragraph" },
  { type: BLOCK_TYPES.CALLOUT, labelKey: "documentForms.blocks.callout" },
  { type: BLOCK_TYPES.DIVIDER, labelKey: "documentForms.blocks.divider" },
  { type: BLOCK_TYPES.TEXT_FIELD, labelKey: "documentForms.blocks.text_field" },
  { type: BLOCK_TYPES.TEXTAREA, labelKey: "documentForms.blocks.textarea" },
  { type: BLOCK_TYPES.NUMBER, labelKey: "documentForms.blocks.number" },
  { type: BLOCK_TYPES.DATE, labelKey: "documentForms.blocks.date" },
  { type: BLOCK_TYPES.CHECKBOX, labelKey: "documentForms.blocks.checkbox" },
  { type: BLOCK_TYPES.SELECT, labelKey: "documentForms.blocks.select" },
  { type: BLOCK_TYPES.CHECKLIST, labelKey: "documentForms.blocks.checklist" },
  { type: BLOCK_TYPES.TABLE, labelKey: "documentForms.blocks.table" },
  { type: BLOCK_TYPES.SIGNATURE, labelKey: "documentForms.blocks.signature" },
];

export function newFormDocumentBlockId() {
  return `block-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function newChecklistItemId() {
  return `item-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function newTableColumnId() {
  return `col-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function createDefaultBlock(type) {
  const id = newFormDocumentBlockId();

  switch (type) {
    case BLOCK_TYPES.HEADING:
      return { id, type, props: { text: "Overskrift", level: 2 } };
    case BLOCK_TYPES.PARAGRAPH:
      return {
        id,
        type,
        props: { content: "<p>Skriv instruksjonstekst her.</p>" },
      };
    case BLOCK_TYPES.CALLOUT:
      return {
        id,
        type,
        props: {
          title: "Informasjon",
          content: "<p>Viktig informasjon til utfyller.</p>",
          variant: "info",
        },
      };
    case BLOCK_TYPES.DIVIDER:
      return { id, type, props: {} };
    case BLOCK_TYPES.TEXT_FIELD:
      return {
        id,
        type,
        props: { label: "Tekstfelt", placeholder: "", required: false },
      };
    case BLOCK_TYPES.TEXTAREA:
      return {
        id,
        type,
        props: { label: "Lang tekst", rows: 4, required: false },
      };
    case BLOCK_TYPES.NUMBER:
      return { id, type, props: { label: "Tall", required: false } };
    case BLOCK_TYPES.DATE:
      return { id, type, props: { label: "Dato", required: false } };
    case BLOCK_TYPES.CHECKBOX:
      return { id, type, props: { label: "Avkryssing", required: false } };
    case BLOCK_TYPES.SELECT:
      return {
        id,
        type,
        props: {
          label: "Valg",
          options: ["Alternativ 1", "Alternativ 2"],
          required: false,
        },
      };
    case BLOCK_TYPES.CHECKLIST:
      return {
        id,
        type,
        props: {
          label: "Sjekkliste",
          items: [
            { id: newChecklistItemId(), label: "Punkt 1" },
            { id: newChecklistItemId(), label: "Punkt 2" },
          ],
        },
      };
    case BLOCK_TYPES.TABLE:
      return {
        id,
        type,
        props: {
          label: "Tabell",
          columns: [
            { id: newTableColumnId(), label: "Kolonne 1", type: "text" },
            { id: newTableColumnId(), label: "Kolonne 2", type: "text" },
          ],
          minRows: 1,
          allowAddRows: true,
          required: false,
        },
      };
    case BLOCK_TYPES.SIGNATURE:
      return { id, type, props: { label: "Signatur" } };
    default:
      return { id, type: BLOCK_TYPES.PARAGRAPH, props: { content: "" } };
  }
}

export function initialValueForBlock(block) {
  if (!block?.id) return "";

  switch (block.type) {
    case BLOCK_TYPES.CHECKBOX:
      return false;
    case BLOCK_TYPES.CHECKLIST: {
      const checked = {};
      (block.props?.items || []).forEach((item) => {
        const key = item.id || item.label;
        checked[key] = false;
      });
      return { checked };
    }
    case BLOCK_TYPES.TABLE: {
      const minRows = Math.max(block.props?.minRows || 1, 1);
      const columns = block.props?.columns || [];
      const rows = Array.from({ length: minRows }, () =>
        columns.reduce((row, col) => {
          row[col.id] = col.type === "checkbox" ? false : "";
          return row;
        }, {})
      );
      return { rows };
    }
    default:
      return "";
  }
}

export function buildInitialValues(blocks = []) {
  return blocks.reduce((acc, block) => {
    if (block?.id) acc[block.id] = initialValueForBlock(block);
    return acc;
  }, {});
}

export function moveBlock(blocks, fromIndex, toIndex) {
  const next = [...blocks];
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next;
}
